'use client';

import { SystemLoadingIcon as Loader2 } from '@/components/ui/SystemLoading';
import React from 'react';
import { motion, useReducedMotion, type HTMLMotionProps } from 'framer-motion';
import { cn } from '@/lib/cn';
import { LiquidGlass } from './liquid-glass/LiquidGlass';
import controls from '@/components/public-site/PublicSiteControls.module.css';
import styles from './StudioAction.module.css';


/* ================================================================
   Delos Button — 极简白灰 · Zima Blue 唯一色彩
   ================================================================ */

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends Omit<HTMLMotionProps<'button'>, 'children'> {
  ref?: React.Ref<HTMLButtonElement>;
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  icon?: React.ReactNode;
  children?: React.ReactNode;
}

const variantStyles: Record<ButtonVariant, string> = {
  primary:
    `${controls.button} ${controls.primary}`,
  secondary:
    `${controls.button} ${controls.secondary}`,
  ghost:
    'bg-transparent text-hr-text-secondary hover:bg-surface-hover hover:text-hr-text-primary',
  danger:
    'bg-semantic-error-bg text-semantic-error hover:bg-red-100 border border-transparent hover:border-red-200',
};

const sizeStyles: Record<ButtonSize, string> = {
  sm: styles.sm,
  md: styles.md,
  lg: styles.lg,
};

export function Button({ variant = 'primary', size = 'md', isLoading, icon, children, className, disabled, ref, ...props }: ButtonProps) {
  const reduceMotion = useReducedMotion();
  return (
    <motion.button
      ref={ref}
      data-studio-control
      data-studio-variant={variant}
      aria-busy={isLoading || undefined}
      whileHover={disabled || isLoading || reduceMotion ? undefined : { scale: 1.015 }}
      whileTap={disabled || isLoading || reduceMotion ? undefined : { scale: 0.975 }}
      transition={{ duration: 0.15 }}
      disabled={disabled || isLoading}
      className={cn(
        'relative isolate inline-flex items-center justify-center rounded-full font-medium transition-colors duration-200 select-none',
        styles.button,
        'focus-visible:ring-2 focus-visible:ring-[#1787B2] focus-visible:ring-offset-2',
        'disabled:opacity-45 disabled:pointer-events-none',
        variantStyles[variant],
        sizeStyles[size],
        className
      )}
      {...props}
    >
      {(variant === 'primary' || variant === 'secondary') && <LiquidGlass as="span" className={styles.lens}>{null}</LiquidGlass>}
      {isLoading ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : icon ? (
        <span className="shrink-0">{icon}</span>
      ) : null}
      {children && <span>{children}</span>}
    </motion.button>
  );
}
